import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ProductInfoDialog from 'components/ReportRequestV2/ProductInfoDialog';

import styles from './SelectedProductsTable.module.css';
import { CatalogProduct } from '../ProductPicker';

export interface SelectedProductsTableProps {
  products: CatalogProduct[];
  allowedWorkspaces: string[];
  minReviews: number;
  onRemove: (name: string) => void;
  /** Allows a workspace's data for every product; offered on workspaces that aren't allowed. */
  onAllowWorkspace?: (workspace: string) => void;
  onClear?: () => void;
  disabled?: boolean;
}

interface ProductGroup {
  name: string;
  rows: CatalogProduct[];
  categories: string[];
  reviews: number;
  original: boolean;
  reachable: boolean;
}

const PAGE_SIZE = 10;

/** "+N not allowed", listing them on hover; their data is not used. */
const NotAllowedNote: React.FC<{ rows: CatalogProduct[]; allowedWorkspaces: string[] }> = ({
  rows,
  allowedWorkspaces,
}) => {
  const others = rows.filter((r) => !allowedWorkspaces.includes(r.workspace)).map((r) => r.workspace);
  if (others.length === 0) return null;
  return (
    <Tooltip title={`Not allowed, so their data is not used: ${others.join(', ')}`}>
      <Typography variant="caption" color="text.secondary" className={styles.otherWorkspaces} tabIndex={0}>
        +{others.length} not allowed
      </Typography>
    </Tooltip>
  );
};

export const groupProducts = (
  products: CatalogProduct[],
  allowedWorkspaces: string[],
): ProductGroup[] => {
  const groups = new Map<string, CatalogProduct[]>();
  products.forEach((p) => groups.set(p.name, [...(groups.get(p.name) ?? []), p]));
  return Array.from(groups.entries()).map(([name, rows]) => ({
    name,
    rows,
    categories: Array.from(new Set(rows.map((r) => r.category).filter(Boolean))),
    reviews: Math.max(...rows.map((r) => r.reviews)),
    original: rows.some((r) => r.original),
    reachable: rows.some((r) => allowedWorkspaces.includes(r.workspace)),
  }));
};

/** The chosen competitive set, one row per product name, with data-quality flags. */
const SelectedProductsTable: React.FC<SelectedProductsTableProps> = ({
  products,
  allowedWorkspaces,
  minReviews,
  onRemove,
  onAllowWorkspace,
  onClear,
  disabled,
}) => {
  const [filter, setFilter] = React.useState('');
  const [info, setInfo] = React.useState<ProductGroup | null>(null);
  const [page, setPage] = React.useState(0);
  const groups = React.useMemo(
    () => groupProducts(products, allowedWorkspaces),
    [products, allowedWorkspaces],
  );
  const visible = groups.filter((g) =>
    g.name.toLowerCase().includes(filter.trim().toLowerCase()),
  );
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const removable = groups.filter((g) => !g.original).length;

  if (groups.length === 0) {
    return (
      <Box className={styles.empty}>
        <Typography color="text.secondary">
          No products yet. Search above or paste a list to build the competitive set.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box className={styles.toolbar}>
        <Typography variant="body2" color="text.secondary">
          {groups.length} product{groups.length === 1 ? '' : 's'}
        </Typography>
        <Box className={styles.toolbarActions}>
          {groups.length > PAGE_SIZE && (
            <TextField
              size="small"
              placeholder="Filter"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(0);
              }}
              inputProps={{ 'aria-label': 'Filter selected products' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {onClear && removable > 0 && (
            <Button size="small" color="inherit" onClick={onClear} disabled={disabled}>
              Remove all
            </Button>
          )}
        </Box>
      </Box>
      <TableContainer>
        <Table size="small" aria-label="Selected products">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Workspaces</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Reviews</TableCell>
              <TableCell align="right">
                <span className={styles.srOnly}>Actions</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible
              .slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)
              .map((g) => {
                const lowData = g.reviews < minReviews;
                return (
                  <TableRow key={g.name} hover>
                    <TableCell>
                      <Box className={styles.name}>
                        {!g.reachable && (
                          <Tooltip
                            title={
                              onAllowWorkspace
                                ? 'Not in any allowed workspace. Allow one of its workspaces to include it.'
                                : 'Not in any allowed workspace'
                            }
                          >
                            <ErrorOutlineIcon color="error" fontSize="small" aria-label="Not in any allowed workspace" />
                          </Tooltip>
                        )}
                        {g.reachable && lowData && (
                          <Tooltip title={`Fewer than ${minReviews} reviews`}>
                            <WarningAmberIcon color="warning" fontSize="small" aria-label="Low data" />
                          </Tooltip>
                        )}
                        <ButtonBase
                          className={styles.nameButton}
                          onClick={() => setInfo(g)}
                          aria-label={`Product information for ${g.name}`}
                        >
                          <span>{g.name}</span>
                          <InfoOutlinedIcon fontSize="inherit" className={styles.infoIcon} />
                        </ButtonBase>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box className={styles.chips}>
                        {g.rows
                          .filter((r) => allowedWorkspaces.includes(r.workspace))
                          .map((r) => (
                            <Chip key={r.id} size="small" label={r.workspace} variant="outlined" />
                          ))}
                        {g.reachable ? (
                          <NotAllowedNote rows={g.rows} allowedWorkspaces={allowedWorkspaces} />
                        ) : (
                          // Only an unreachable product offers to allow a workspace, so other
                          // clients' data is never one stray click away.
                          g.rows.map((r) => (
                            <Tooltip
                              key={r.id}
                              title={onAllowWorkspace ? `Allow ${r.workspace} data for every product` : 'Not allowed'}
                            >
                              <Chip
                                size="small"
                                label={r.workspace}
                                variant="outlined"
                                color="error"
                                className={styles.notAllowed}
                                icon={onAllowWorkspace ? <AddIcon /> : undefined}
                                onClick={onAllowWorkspace && !disabled ? () => onAllowWorkspace(r.workspace) : undefined}
                                aria-label={onAllowWorkspace ? `Allow ${r.workspace}` : `${r.workspace} (not allowed)`}
                              />
                            </Tooltip>
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {g.categories.length > 0 ? (
                        g.categories.join(', ')
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Uncategorised
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{g.reviews}</TableCell>
                    <TableCell align="right">
                      {g.original ? (
                        <Tooltip title="Already on the report">
                          <LockOutlinedIcon fontSize="small" color="disabled" aria-label="Already on the report" />
                        </Tooltip>
                      ) : (
                        <IconButton
                          size="small"
                          aria-label={`Remove ${g.name}`}
                          onClick={() => onRemove(g.name)}
                          disabled={disabled}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No selected products match “{filter}”.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {visible.length > PAGE_SIZE && (
        <TablePagination
          component="div"
          count={visible.length}
          page={currentPage}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, p) => setPage(p)}
        />
      )}
      {info && (
        <ProductInfoDialog product={info} allowedWorkspaces={allowedWorkspaces} onClose={() => setInfo(null)} />
      )}
    </Box>
  );
};

export default SelectedProductsTable;
